import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { canCreateProject } from 'utils/rbacUtils';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    IconButton,
    LinearProgress,
    Divider
} from '@mui/material';
import {
    Storage as StorageIcon,
    Code as CodeIcon,
    AccountTree as WorkflowIcon,
    Add as AddIcon,
    PlayArrow as PlayIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    People as PeopleIcon,
    Folder as FolderIcon
} from '@mui/icons-material';

// Import mock data
import {
    dashboardMetrics,
    projectsData,
    jupyterHubData,
    airflowData,
    userData,
    mongodbData
} from '../../dummy_data/datahubMockData';

const DataHubDashboard = () => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const account = useSelector((state) => state.account);
    const canCreate = canCreateProject(account?.user);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy':
            case 'running':
            case 'active':
            case 'success':
                return 'success';
            case 'warning':
            case 'stopped':
                return 'warning';
            case 'error':
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'healthy':
            case 'running':
            case 'active':
            case 'success':
                return <CheckCircleIcon color="success" />;
            case 'warning':
            case 'stopped':
                return <WarningIcon color="warning" />;
            case 'error':
            case 'failed':
                return <ErrorIcon color="error" />;
            default:
                return <WarningIcon color="default" />;
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom>
                    DataHub Platform Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Welcome back, {userData.currentUser.name}! • Last updated: {currentTime.toLocaleTimeString()}
                </Typography>
            </Box>

            {/* System Health Overview */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                System Health Overview
                            </Typography>
                            <Grid container spacing={2}>
                                {dashboardMetrics.systemHealth.services.map((service, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusIcon(service.status)}
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {service.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Uptime: {service.uptime}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Quick Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    <PeopleIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{dashboardMetrics.usageStats.activeUsers}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Active Users
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                    <FolderIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{dashboardMetrics.usageStats.totalProjects}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Projects
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'success.main' }}>
                                    <StorageIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{dashboardMetrics.usageStats.totalStorage}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Storage
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'info.main' }}>
                                    <CodeIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4">{dashboardMetrics.usageStats.totalNotebooks}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Notebooks
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Projects and Services */}
            <Grid container spacing={3}>
                {/* Projects Overview */}
                <Grid item xs={12} lg={8}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Recent Projects</Typography>
                                {canCreate && (
                                    <Button variant="contained" startIcon={<AddIcon />}>
                                        Create Project
                                    </Button>
                                )}
                            </Box>
                            <List>
                                {projectsData.projects.map((project, index) => (
                                    <React.Fragment key={project.id}>
                                        <ListItem>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: getStatusColor(project.status) + '.main' }}>
                                                    <FolderIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle1">{project.name}</Typography>
                                                        <Chip label={project.status} size="small" color={getStatusColor(project.status)} />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box component="div">
                                                        <Typography variant="body2" color="text.secondary" component="span" display="block">
                                                            {project.description}
                                                        </Typography>
                                                        <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                                            <Typography variant="caption" component="span">
                                                                Storage: {project.storage.used} / {project.storage.limit}
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={project.storage.percentage}
                                                                sx={{ width: 100, height: 6 }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                }
                                                secondaryTypographyProps={{ component: 'div' }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton size="small" color="primary">
                                                    <PlayIcon />
                                                </IconButton>
                                                <IconButton size="small" color="secondary">
                                                    <CodeIcon />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                        {index < projectsData.projects.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Services Status */}
                <Grid item xs={12} lg={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Services Status
                            </Typography>

                            {/* JupyterHub */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">JupyterHub</Typography>
                                    <Chip label={jupyterHubData.status} size="small" color={getStatusColor(jupyterHubData.status)} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    Running servers: {jupyterHubData.servers.filter((s) => s.status === 'running').length}
                                </Typography>
                            </Box>

                            {/* Airflow */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">Airflow</Typography>
                                    <Chip label={airflowData.status} size="small" color={getStatusColor(airflowData.status)} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    Active DAGs: {airflowData.dags.filter((d) => d.status === 'active').length}
                                </Typography>
                            </Box>

                            {/* MongoDB */}
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">MongoDB</Typography>
                                    <Chip label={mongodbData.status} size="small" color={getStatusColor(mongodbData.status)} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    Databases: {mongodbData.databases.length}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Quick Actions */}
                            <Typography variant="subtitle2" gutterBottom>
                                Quick Actions
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Button variant="outlined" size="small" startIcon={<CodeIcon />}>
                                    Start JupyterHub
                                </Button>
                                <Button variant="outlined" size="small" startIcon={<WorkflowIcon />}>
                                    Open Airflow
                                </Button>
                                <Button variant="outlined" size="small" startIcon={<StorageIcon />}>
                                    Manage Storage
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Recent Activity */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Recent Activity
                            </Typography>
                            <List>
                                {dashboardMetrics.recentActivity.map((activity, index) => (
                                    <ListItem key={index}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: 'grey.200' }}>
                                                {activity.type === 'file_upload' && <StorageIcon />}
                                                {activity.type === 'notebook_created' && <CodeIcon />}
                                                {activity.type === 'workflow_completed' && <WorkflowIcon />}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={activity.description}
                                            secondary={`${activity.user} • ${formatDate(activity.timestamp)}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DataHubDashboard;
